import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersApi } from '../services/api/users';
import { isEmail, required, matches } from '../utils/validators';

export interface User {
  id: number;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'manager' | 'client';
  active: boolean;
  created_at: string;
}

export interface FormData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  role: string;
}

export interface UserFieldErrors {
  name: string[];
  email: string[];
  password: string[];
  password_confirmation: string[];
}

export interface UseUsersListReturn {
  loading: boolean;
  users: User[];
  currentUser: User | null;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  roleFilter: string;
  setRoleFilter: (value: string) => void;
  searchName: string;
  setSearchName: (value: string) => void;
  searchEmail: string;
  setSearchEmail: (value: string) => void;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  itemsPerPage: number;
  setItemsPerPage: React.Dispatch<React.SetStateAction<number>>;
  filteredUsers: User[];
  paginatedItems: User[];
  totalPages: number;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  editingUser: User | null;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  fieldErrors: UserFieldErrors;
  formErrors: string[];
  submitting: boolean;
  handleOpenModal: (user?: User) => void;
  validate: () => boolean;
  handleSubmit: () => Promise<void>;
  handleToggleStatus: (user: User) => Promise<void>;
  canEditUser: (targetUser: User) => boolean;
  canToggleUserStatus: (targetUser: User) => boolean;
  canCreateUser: () => boolean;
  loadUsers: () => Promise<void>;
}

export const useUsersList = (): UseUsersListReturn => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Read current user from localStorage once (client state)
  const [currentUser] = useState<User | null>(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try { return JSON.parse(userStr); } catch { return null; }
    }
    return null;
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchName, setSearchName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    role: 'manager',
  });
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<UserFieldErrors>({
    name: [],
    email: [],
    password: [],
    password_confirmation: [],
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);

  // ===== Server state via TanStack Query =====
  const { data, isPending } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => usersApi.getAdminUsers(),
  });

  const users = useMemo(() => {
    if (!data?.users) return [];
    // Filter out clients just in case the API returns them
    return data.users.filter((u: { role: string }) =>
      ['owner', 'admin', 'manager'].includes(u.role),
    );
  }, [data]);

  // ===== Mutations =====
  const invalidateUsers = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: (fd: FormData) => usersApi.createAdminUser(fd),
    onSuccess: invalidateUsers,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data: updateData }: { id: number | string; data: any }) =>
      usersApi.updateAdminUser(id, updateData),
    onSuccess: invalidateUsers,
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number | string) => usersApi.deactivateAdminUser(id),
    onSuccess: invalidateUsers,
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: number | string) => usersApi.reactivateAdminUser(id),
    onSuccess: invalidateUsers,
  });

  // Reset to first page when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, roleFilter, searchName, searchEmail]);

  const filteredUsers = useMemo(() => {
    return users.filter((user: User) => {
      const matchesName =
        user.name?.toLowerCase().includes(searchName.toLowerCase()) ?? true;
      const matchesEmail =
        user.email?.toLowerCase().includes(searchEmail.toLowerCase()) ?? true;

      const matchesStatus =
        statusFilter === 'all'
          ? true
          : statusFilter === 'active'
            ? user.active
            : !user.active;

      const matchesRole =
        roleFilter === 'all' ? true : user.role === roleFilter;

      return matchesName && matchesEmail && matchesStatus && matchesRole;
    });
  }, [users, searchName, searchEmail, statusFilter, roleFilter]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = useMemo(
    () => filteredUsers.slice(startIndex, endIndex),
    [filteredUsers, startIndex, endIndex],
  );
  const totalPages = useMemo(
    () => Math.ceil(filteredUsers.length / itemsPerPage),
    [filteredUsers.length, itemsPerPage],
  );

  // Can edit user (name, email, password, role)
  const canEditUser = useCallback(
    (targetUser: User): boolean => {
      if (!currentUser) return false;

      // Only owner can edit themselves (self-editing allowed for owner)
      if (targetUser.role === 'owner' && targetUser.id !== currentUser.id)
        return false;

      // Owner can edit anyone including themselves
      if (currentUser.role === 'owner') return true;

      // Admin can edit themselves and other admins/managers
      if (currentUser.role === 'admin') {
        return ['admin', 'manager'].includes(targetUser.role);
      }

      return false;
    },
    [currentUser],
  );

  // Can toggle user status (activate/deactivate)
  const canToggleUserStatus = useCallback(
    (targetUser: User): boolean => {
      if (!currentUser) return false;
      if (targetUser.id === currentUser.id) return false; // Cannot toggle own status
      if (targetUser.role === 'owner') return false; // Cannot toggle owner status

      // Owner can toggle anyone except self and owner (covered above)
      if (currentUser.role === 'owner') return true;

      // Admin can toggle other admins and managers
      if (currentUser.role === 'admin') {
        return ['admin', 'manager'].includes(targetUser.role);
      }

      return false;
    },
    [currentUser],
  );

  const canCreateUser = useCallback((): boolean => {
    if (!currentUser) return false;
    return currentUser.role === 'owner' || currentUser.role === 'admin';
  }, [currentUser]);

  const handleOpenModal = useCallback((user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        password_confirmation: '',
        role: user.role as string,
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        role: 'manager', // Default role
      });
    }
    setFieldErrors({
      name: [],
      email: [],
      password: [],
      password_confirmation: [],
    });
    setFormErrors([]);
    setIsModalOpen(true);
  }, []);

  const validate = useCallback(() => {
    const nextFieldErrors: UserFieldErrors = {
      name: [],
      email: [],
      password: [],
      password_confirmation: [],
    };
    const nextFormErrors: string[] = [];

    const nameError = required(formData.name);
    if (nameError) {
      nextFieldErrors.name.push(nameError);
      nextFormErrors.push(nameError);
    }

    const emailRequiredError = required(formData.email);
    if (emailRequiredError) {
      nextFieldErrors.email.push(emailRequiredError);
      nextFormErrors.push(emailRequiredError);
    } else {
      const emailError = isEmail(formData.email);
      if (emailError) {
        nextFieldErrors.email.push(emailError);
        nextFormErrors.push(emailError);
      }
    }

    const passwordProvided = formData.password.trim().length > 0;
    if (!editingUser || passwordProvided) {
      const passwordError = required(formData.password);
      if (passwordError) {
        nextFieldErrors.password.push(passwordError);
        nextFormErrors.push(passwordError);
      }
    }

    if (passwordProvided) {
      const confirmationError = required(formData.password_confirmation);
      if (confirmationError) {
        nextFieldErrors.password_confirmation.push(confirmationError);
        nextFormErrors.push(confirmationError);
      } else {
        const mismatchError = matches(
          formData.password_confirmation,
          formData.password,
          'admin.users.password_mismatch',
        );
        if (mismatchError) {
          nextFieldErrors.password_confirmation.push(mismatchError);
          nextFormErrors.push(mismatchError);
        }
      }
    }

    setFieldErrors(nextFieldErrors);
    setFormErrors(nextFormErrors);

    return nextFormErrors.length === 0;
  }, [editingUser, formData]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      if (editingUser) {
        const updateData: any = { name: formData.name, email: formData.email, role: formData.role };
        if (formData.password) {
          updateData.password = formData.password;
          updateData.password_confirmation = formData.password_confirmation;
        }
        await updateMutation.mutateAsync({ id: editingUser.id, data: updateData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setFieldErrors({
        name: [],
        email: [],
        password: [],
        password_confirmation: [],
      });
      setFormErrors([]);
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to save user:', err);
      setFormErrors(['admin.users.save_failed']);
    } finally {
      setSubmitting(false);
    }
  }, [editingUser, formData, createMutation, updateMutation, validate]);

  const handleToggleStatus = useCallback(
    async (user: User) => {
      const confirmMessage = user.active
        ? t('admin.users.confirmDeactivate')
        : t('admin.users.confirmReactivate');

      if (!window.confirm(confirmMessage)) return;

      try {
        if (user.active) {
          await deactivateMutation.mutateAsync(user.id);
        } else {
          await reactivateMutation.mutateAsync(user.id);
        }
      } catch (err) {
        console.error(`Failed to toggle user status:`, err);
      }
    },
    [deactivateMutation, reactivateMutation, t],
  );

  // loadUsers is now a query invalidation helper (replaces manual refetch)
  const loadUsers = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  }, [queryClient]);

  return {
    loading: isPending,
    users,
    currentUser,
    statusFilter,
    setStatusFilter,
    roleFilter,
    setRoleFilter,
    searchName,
    setSearchName,
    searchEmail,
    setSearchEmail,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    filteredUsers,
    paginatedItems,
    totalPages,
    isModalOpen,
    setIsModalOpen,
    editingUser,
    formData,
    setFormData,
    fieldErrors,
    formErrors,
    submitting,
    handleOpenModal,
    validate,
    handleSubmit,
    handleToggleStatus,
    canEditUser,
    canToggleUserStatus,
    canCreateUser,
    loadUsers,
  };
};
